import React, { FormEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { useActivity } from '../activity.context';
import { useAlerts } from '../../../hooks/useAlerts';
import { useAuthUsers, useDomainPath } from '../../../auth/auth.context';
import { get, post, put, httpDelete, widgetLoaderUrl } from '../../../fetch';
import { DbDocumentSelect } from '../../../components/DbDocumentSelect.components';
import { PricingIdsMapper } from './PricingIdsMapper.component';
import { findUserGroupPricing, USER_GROUPS } from '@crm/utils';
import { WidgetConfigurationDialog } from './WidgetConfigurationDialog.component';

/**
 * Formulaire de configuration des intégrations externes pour les activités
 * 
 * Ce composant permet de :
 * - Configurer l'intégration WooCommerce pour les activités
 * - Définir les identifiants externes et les groupes de tarification
 * - Mapper les prix entre systèmes internes et externes
 * - Générer les liens de réservation pour chaque groupe tarifaire
 * 
 * Logique métier :
 * - Intégration WooCommerce avec identifiants externes multiples
 * - Gestion des groupes de tarification pour paiements complets et partiels
 * - Mapping des prix entre systèmes via PricingIdsMapper
 * - Génération automatique des liens de réservation par groupe
 * - Validation des groupes de tarification autorisés
 * 
 * @returns Composant React du formulaire d'intégrations
 */
export const IntegrationsForm: React.FC = () => {
  const { activity } = useActivity();
  const { notify } = useAlerts();
  const { domain, groups } = useAuthUsers();
  const path = useDomainPath(`/activity-integration`);
  const userGroupPath = useDomainPath('/user-group');

  /**
   * États locaux pour la gestion de l'intégration
   */
  const [groupNames, setGroupNames] = useState<CRM.Dictionary<string>>({});
  const [integrationId, setIntegrationId] = useState<string>();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [externalActivityIds, setExternalActivityIds] = useState<string[]>();
  const [userGroupId, setUserGroupId] = useState<string>();
  const [
    userGroupIdWhenPartialPayment,
    setUserGroupIdWhenPartialPayment,
  ] = useState<string>();
  const [activityDateKey, setActivityDateKey] = useState<string>();
  const [activityTimeKey, setActivityTimeKey] = useState<string>();
  const [mapping, setMapping] = useState<CRM.PricingIdsMapping>({});

  /**
   * États pour la gestion des widgets
   */
  const widgetConfigPath = useDomainPath(
    `/activities/${activity._id}/widget-configuration`,
  );
  const [widgetConfigs, setWidgetConfigs] = useState<
    CRM.WidgetConfigurationDocument[]
  >([]);
  const [widgetDialogOpen, setWidgetDialogOpen] = useState<boolean>(false);
  const [selectedPricingGroup, setSelectedPricingGroup] = useState<
    string | null
  >(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>(
    undefined,
  );

  /**
   * Filtre les groupes de tarification autorisés pour cette activité
   */
  const allPricingGroups = groups
    .concat({
      _id: USER_GROUPS.NORMAL,
      domain: '',
      displayName: 'Normal',
      login: 'normal',
      role: '',
    } as any)
    .filter(group =>
      activity?.pricing?.some(p => findUserGroupPricing(p, group._id)),
    );

  /**
   * Liste complète des pricingGroups pour les widgets (userGroups + 'internet')
   * Exclut 'normal' car il n'a pas besoin de widget
   */
  const widgetPricingGroups = allPricingGroups.filter(
    group => group._id !== USER_GROUPS.NORMAL,
  );

  /**
   * Vérifie si le groupe de tarification sélectionné est autorisé
   */
  const isAllowedPricingGroup =
    userGroupId &&
    activity?.pricing?.some(p => findUserGroupPricing(p, userGroupId));

  /**
   * Effect pour charger les noms des groupes utilisateurs
   */
  useEffect(() => {
    get<CRM.UserGroupDocument[]>(userGroupPath).then(groups => {
      setGroupNames(
        groups.reduce((acc, group) => {
          acc[group._id] = group.displayName;
          return acc;
        }, {} as CRM.Dictionary<string>),
      );
    });
  }, [setGroupNames, userGroupPath]);

  /**
   * Effect pour charger la configuration d'intégration existante
   */
  useEffect(() => {
    get<CRM.ActivityIntegrationDocument>(`${path}/${activity._id}`).then(
      integration => {
        if (!integration) {
          return;
        }
        setIntegrationId(integration._id);
        setEnabled(integration.enabled);
        setExternalActivityIds(integration.externalActivityIds);
        setUserGroupId(integration.pricingGroupId);
        setUserGroupIdWhenPartialPayment(
          integration.pricingGroupIdWhenPartialPayment,
        );
        setMapping(integration.pricingMapping);
        setActivityTimeKey(integration.activityTimeKey);
        setActivityDateKey(integration.activityDateKey);
      },
    );
  }, [path, activity._id]);

  /**
   * Effect pour charger les configurations de widget existantes
   */
  useEffect(() => {
    if (activity._id) {
      get<CRM.WidgetConfigurationDocument[]>(widgetConfigPath)
        .then(configs => {
          setWidgetConfigs(configs);
        })
        .catch(() => {
          // Si erreur (404 ou autre), initialiser avec tableau vide
          setWidgetConfigs([]);
        });
    }
  }, [activity._id, widgetConfigPath]);

  /**
   * Fonction de sauvegarde de la configuration d'intégration
   * 
   * @param event Événement de soumission du formulaire
   */
  const save = async (event: FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      if (
        !activity._id ||
        !externalActivityIds ||
        !userGroupId ||
        !activityDateKey ||
        !activityTimeKey
      ) {
        notify('Remplir tous les champs avant de sauvegarder', 'error');
        return;
      }
      const data: CRM.CreateActivityIntegration = {
        kind: 'woocommerce',
        enabled: enabled,
        activity: activity._id,
        externalActivityIds: externalActivityIds,
        pricingGroupId: userGroupId,
        pricingGroupIdWhenPartialPayment: userGroupIdWhenPartialPayment,
        pricingMapping: mapping,
        activityDateKey: activityDateKey,
        activityTimeKey: activityTimeKey,
      };
      if (integrationId) {
        await put<CRM.CreateActivityIntegration>(
          `${path}/${integrationId}`,
          data,
        );
      } else {
        await post<CRM.CreateActivityIntegration>(path, data);
      }
      notify('Le document a été mis à jour', 'success');
    } catch (err) {
      notify('Impossible de mettre à jour le document', 'error');
    }
  };

  /**
   * Fonction de changement du groupe de tarification principal
   * 
   * @param group Groupe utilisateur sélectionné
   */
  const changeUserGroupId = (group?: CRM.UserGroupDocument) => {
    if (!group) {
      return setUserGroupId(undefined);
    }
    setUserGroupId(group._id);
  };

  /**
   * Fonction de changement du groupe de tarification pour paiement partiel
   * 
   * @param group Groupe utilisateur sélectionné
   */
  const changeUserGroupIdWhenPartialPayment = (
    group?: CRM.UserGroupDocument,
  ) => {
    if (!group) {
      return setUserGroupIdWhenPartialPayment(undefined);
    }
    setUserGroupIdWhenPartialPayment(group._id);
  };

  /**
   * Ouvre le dialog de configuration widget pour un pricingGroup
   * @param pricingGroup ID du pricingGroup
   * @param widgetId ID du widget à éditer (optionnel, si non fourni = création)
   */
  const handleOpenWidgetDialog = (
    pricingGroup: string,
    widgetId?: string,
  ) => {
    if (!activity._id) {
      notify(
        'L\'activité doit être sauvegardée avant de configurer le widget',
        'error',
      );
      return;
    }
    setSelectedPricingGroup(pricingGroup);
    setSelectedWidgetId(widgetId);
    setWidgetDialogOpen(true);
  };

  /**
   * Ferme le dialog et recharge les configurations
   */
  const handleCloseWidgetDialog = () => {
    setWidgetDialogOpen(false);
    setSelectedPricingGroup(null);
    setSelectedWidgetId(undefined);
  };

  /**
   * Recharge les configurations après sauvegarde
   */
  const handleWidgetSaved = () => {
    if (activity._id) {
      get<CRM.WidgetConfigurationDocument[]>(widgetConfigPath)
        .then(configs => {
          setWidgetConfigs(configs);
        })
        .catch(() => {
          setWidgetConfigs([]);
        });
    }
  };

  /**
   * Supprime un widget
   */
  const handleDeleteWidget = async (widgetId: string) => {
    if (!activity._id) return;

    try {
      await httpDelete(`${widgetConfigPath}/${widgetId}`);
      notify('Widget supprimé avec succès', 'success');
      handleWidgetSaved();
    } catch (err) {
      notify('Erreur lors de la suppression du widget', 'error');
    }
  };

  /**
   * Récupère tous les widgets d'un pricingGroup
   */
  const getWidgetsForPricingGroup = (
    pricingGroup: string,
  ): CRM.WidgetConfigurationDocument[] => {
    return widgetConfigs.filter(
      config => config.pricingGroup === pricingGroup,
    );
  };

  /**
   * Récupère le nom d'affichage d'un pricingGroup
   */
  const getPricingGroupName = (pricingGroup: string): string => {
    if (pricingGroup === 'internet') {
      return 'Internet';
    }
    return groupNames[pricingGroup] || pricingGroup;
  };

  return (
    <form onSubmit={save}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5">Widget</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Configurez les widgets de réservation pour chaque groupe tarifaire.
            Les widgets peuvent être intégrés sur votre site ou celui de vos partenaires.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {widgetPricingGroups.map(pricingGroup => {
            const pricingGroupId = pricingGroup._id;
            const widgets = getWidgetsForPricingGroup(pricingGroupId);
            return (
              <Box
                key={pricingGroupId}
                mb={3}
                p={2}
                border={1}
                borderColor="divider"
                borderRadius={4}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Box flexGrow={1}>
                    <Typography variant="body1">
                      <strong>
                        {pricingGroupId === 'internet' ? 'Tarif :' : 'Tarif partenaire :'}
                      </strong>{' '}
                      {getPricingGroupName(pricingGroupId)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleOpenWidgetDialog(pricingGroupId)}
                  >
                    Créer un nouveau widget
                  </Button>
                </Box>
                {widgets.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Widgets existants ({widgets.length})
                    </Typography>
                    {widgets.map((widget, index) => (
                      <Box
                        key={widget._id}
                        display="flex"
                        alignItems="center"
                        mb={1}
                        p={1.5}
                        bgcolor="grey.50"
                        borderRadius={2}
                      >
                        <Box flexGrow={1}>
                          <Typography variant="body2">
                            <strong>Code: {widget.code}</strong>
                            {' - '}
                            Couleur: {widget.primaryColor} | Langue:{' '}
                            {widget.lang === 'fr' ? 'Français' : 'English'} |
                            Domaines: {widget.allowedDomains.length}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() =>
                            handleOpenWidgetDialog(pricingGroupId, widget._id)
                          }
                          style={{ marginRight: 8 }}
                        >
                          Éditer
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            const code = `<div id="naturesa-booking-widget"
     data-activity-id="${activity.name}"
     data-pricing-group="${widget.pricingGroup}"
     data-widget-code="${widget.code}"
     data-domain-name="${domain?.name || ''}">
</div>
<script src="${widgetLoaderUrl}/loader.js"></script>`;
                            navigator.clipboard.writeText(code);
                            notify('Code copié dans le presse-papiers', 'success');
                          }}
                          style={{ marginRight: 8 }}
                        >
                          Copier le code
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            const buttonText = widget.lang === 'en' ? 'Book' : 'Réserver';
                            const code = `<div id="naturesa-booking-widget"
     data-activity-id="${activity.name}"
     data-pricing-group="${widget.pricingGroup}"
     data-widget-code="${widget.code}"
     data-domain-name="${domain?.name || ''}"
     data-display-mode="button"
     data-button-text="${buttonText}">
</div>
<script src="${widgetLoaderUrl}/loader.js"></script>`;
                            navigator.clipboard.writeText(code);
                            notify('Code copié dans le presse-papiers', 'success');
                          }}
                          style={{ marginRight: 8 }}
                        >
                          Copier le code (version bouton)
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => handleDeleteWidget(widget._id)}
                        >
                          Supprimer
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <br />
          <Typography variant="h5">Woocommerce</Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            label="Activé"
            labelPlacement="end"
            control={
              <Checkbox
                color="secondary"
                onChange={event => setEnabled(event.target.checked)}
                checked={enabled}
              />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Identifants externes (ex: 3752,3820,3922)"
            variant="outlined"
            value={(externalActivityIds || []).join(',')}
            onChange={event =>
              setExternalActivityIds(
                event.target.value.split(',').map(id => id.trim()),
              )
            }
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DbDocumentSelect
            fullWidth
            label="Tarification du groupe"
            selectedId={userGroupId}
            docs={allPricingGroups}
            onChange={changeUserGroupId}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DbDocumentSelect
            fullWidth
            label="Tarification du groupe lors d'un paiement par acompte"
            selectedId={userGroupIdWhenPartialPayment}
            docs={allPricingGroups}
            onChange={changeUserGroupIdWhenPartialPayment}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Clé contenant la date"
            placeholder={'Choisissez la date de votre descente'}
            variant="outlined"
            value={activityDateKey || ''}
            onChange={event => setActivityDateKey(event.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Clé contenant l'heure"
            placeholder={'Heure de départ'}
            variant="outlined"
            value={activityTimeKey || ''}
            onChange={event => setActivityTimeKey(event.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <PricingIdsMapper
            allPricing={activity.pricing}
            value={mapping}
            onChange={setMapping}
          />
        </Grid>
        {!isAllowedPricingGroup && (
          <Box display="block">
            <Typography color={'error'}>
              Ce point de vente n'a pas de tarif défini pour tous les types de
              places
            </Typography>
          </Box>
        )}
        <Grid item xs={12}>
          <Button
            color="secondary"
            variant="contained"
            type="submit"
            disabled={!isAllowedPricingGroup}
          >
            Enregistrer
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <br/>
          <Typography variant="h5">Tunnel de réservations</Typography>
        </Grid>
        <Grid item xs={12}>
          {activity.pricing && activity.pricing[0].forUserGroups.map((group) => {
            if(group.userGroupIds.length === 0) {
              return <></>;
            }
            return (
              <Typography style={{lineHeight:"1.5rem"}}>
                Lien de réservation (tarif {groupNames[group.userGroupIds[0]] === undefined ? group.userGroupIds[0] : groupNames[group.userGroupIds[0]]}) : &nbsp;
                <Link
                  href={"https://" + domain?.marketUrl + '/activites/' + activity?.name + "?pricing-group="+group.userGroupIds[0]}
                  rel="noopener"
                  variant="inherit"
                  color="secondary"
                  target="_blank"
                  onClick={() => {
                    console.info("Lien vers le tunnel");
                  }}
                >
                  {"https://" + domain?.marketUrl + '/activites/' + activity?.name + "?pricing-group="+group.userGroupIds[0]}
                </Link>
              </Typography>
            );
          })}
        </Grid>
      </Grid>

      {selectedPricingGroup && activity._id && (() => {
        const activityId = activity._id!;
        return (
          <WidgetConfigurationDialog
            open={widgetDialogOpen}
            onClose={handleCloseWidgetDialog}
            activityId={activityId}
            activityName={activity.name || ''}
            pricingGroup={selectedPricingGroup}
            pricingGroupName={getPricingGroupName(selectedPricingGroup)}
            widgetId={selectedWidgetId}
            onSaved={handleWidgetSaved}
          />
        );
      })()}
    </form>
  );
};
